import React, { Component } from 'react';
import fetch from 'cross-fetch';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { Alert } from '@material-ui/lab';
import Snackbar from '@material-ui/core/Snackbar';
import CountrySelect from './CountrySelect.jsx';
import TextField from '../bits/TextField.jsx';
import PasswordTextField from '../bits/PasswordTextField.jsx';

import NavBar from './NavBar.jsx';

const FIELD_ERROR_TYPE_TEMPLATES = {
  unknown: 'account_create_form.field_error_unknown',
  invalid: 'account_create_form.field_error_invalid_format',
  blank: 'account_create_form.field_error_blank_value',
  too_short: 'account_create_form.field_error_too_short',
  email_mismatch: 'account_create_form.field_error_email_mismatch',
};

const emailsMatch = (emailA, emailB) => {
  if (!emailA || !emailB) {
    return false;
  }

  // return true if same characters, case insensitive, with whitespace trimmed
  return emailA.toLowerCase().replace(/\s/g, '') === emailB.toLowerCase().replace(/\s/g, '');
}

class AccountCreateForm extends Component {
  state = {
    isLoading: false,
    generalErrorCode: null,
    fieldErrors: {},
    emailConfirm: '',
  }

  nextDisabled = () => {
    const {
      firstName, lastName, password, countryCode,
    } = this.props.values;

    const { emailConfirm } = this.state;

    return firstName.length === 0 || lastName.length === 0 || password.length === 0 || emailConfirm.length === 0 || !countryCode;
  }

  onSubmit = () => {
    const {
      email, password, firstName, lastName, countryCode,
    } = this.props.values;

    const { 
      emailConfirm,
    } = this.state;

    const { onNext } = this.props;

    if (!emailsMatch(email, emailConfirm)) {
      this.setState({
        generalErrorCode: 'account_create_form.error_generic',
        fieldErrors: { email_confirm: 'email_mismatch'},
      });
      return;
    }

    this.setState({
      isLoading: true,
    });

    let responseCode = 0;

    fetch('/users', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        email,
        password,
        firstName,
        lastName,
        countryCode,
      }),
    })
      .then((response) => {
        responseCode = response.status;
        return response.json();
      })
      .then((json) => {
        if (responseCode === 200) {
          this.setState({ isLoading: false });
          onNext();
          return;
        }

        this.setState({
          generalErrorCode: 'account_create_form.error_generic',

          // Object where key is field name and value is error type (blank, too_short etc)
          fieldErrors: json.fieldErrors,
          isLoading: false,
        });
      })
      .catch(() => {
        // Unexpected error state (e.g if we get back a 500 and a non json payload)
        this.setState({
          generalErrorCode: 'account_create_form.error_generic',
          isLoading: false,
        });
      });
  };

  handleClickShowPassword = () => {
    this.setState(state => ({ showPassword: !state.showPassword }));
  }

  handleErrorToastClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    this.setState({ generalErrorCode: null });
  }

  fieldErrorDescription = (errorType) => {
    const { t } = this.props;

    if (!errorType) {
      return null;
    }

    const templateId = FIELD_ERROR_TYPE_TEMPLATES[errorType]
      || FIELD_ERROR_TYPE_TEMPLATES.unknown;

    return t(templateId);
  }

  render() {
    const {
      values,
      handleChange,
      onBack,
      t,
    } = this.props;

    const { emailConfirm } = this.state;

    return (
      <div>
        <h2>{t('account_create_form.title')}</h2>
        <Snackbar
          open={Boolean(this.state.generalErrorCode)}
          autoHideDuration={6000}
          onClose={this.handleErrorToastClose}
          >
          <Alert onClose={this.handleErrorToastClose} severity="error" variant="filled">
            {t(this.state.generalErrorCode)}
          </Alert>
        </Snackbar>

        <div className="form-group">
          <TextField
            id="firstName"
            label={t('account_create_form.first_name_input_label')}
            value={values.firstName}
            onChange={handleChange('firstName')}
            fullWidth
            margin="normal"
            inputProps={{ style: { textTransform: 'capitalize' } }}
            error={Boolean(this.state.fieldErrors.first_name)}
            helperText={this.fieldErrorDescription(this.state.fieldErrors.first_name)}
          />

          <TextField
            id="lastName"
            label={t('account_create_form.last_name_input_label')}
            value={values.lastName}
            onChange={handleChange('lastName')}
            fullWidth
            margin="normal"
            inputProps={{ style: { textTransform: 'capitalize' } }}
            error={Boolean(this.state.fieldErrors.last_name)}
            helperText={this.fieldErrorDescription(this.state.fieldErrors.last_name)}
          />

          <TextField
            id="email"
            label={t('account_create_form.email_input_label')}
            value={emailConfirm}
            onChange={(e) => this.setState({ emailConfirm: e.target.value })}
            fullWidth
            margin="normal"
            error={Boolean(this.state.fieldErrors.email_confirm)}
            helperText={this.fieldErrorDescription(this.state.fieldErrors.email_confirm)}
          />

          <PasswordTextField
            id="password"
            label={t('account_create_form.password_input_label')}
            value={values.password}
            onChange={handleChange('password')}
            error={Boolean(this.state.fieldErrors.password)}
            helperText={this.fieldErrorDescription(this.state.fieldErrors.password)}
            />

          <CountrySelect
            label={t('account_create_form.country_input_label')}
            onChange={code => handleChange('countryCode')({ target: { value: code } }) }
            defaultToGeoCountry={true}
          />
        </div>

        <NavBar
          isLoading={this.state.isLoading}
          onNext={this.onSubmit}
          nextDisabled={this.nextDisabled()}
          onBack={onBack} />
      </div>
    );
  }
}

AccountCreateForm.propTypes = {
  onNext: PropTypes.func,
  onBack: PropTypes.func,
  handleChange: PropTypes.func,
  values: PropTypes.object,
};

export default withTranslation('activation_wizard')(AccountCreateForm);
